import {
  SKETCH_EXTENT,
  type FloorPlanSketch,
  type SketchArea,
  type SketchAreaKind,
  type SketchDesk,
  type SketchFrame,
  type SketchIcon,
  type SketchLabel,
  type SketchInk,
  type SketchPoint,
  type SketchRoom,
  type SketchScaleSource,
  type SketchWall,
  type SketchWallKind,
} from '../models/floorplan-sketch';
import {
  boxHeightOf,
  boxWidthOf,
  closed,
  dilate,
  fillPolygon,
  labelRegions,
  lineDirectionsOf,
  lineEndsOf,
  medianOf,
  opened,
  orientedBoxOf,
  rotateBitmap,
  runLengthPercentileOf,
  simplifiedRing,
  skewCorrectionOf,
  straightened,
  strokeOutline,
  thinned,
  topLeftOf,
  traceBoundary,
  turnOf,
  type Bitmap,
  type LineDirection,
  type Labelled,
  type OrientedBox,
  type Pixel,
  type Region,
} from './raster';
import {
  SYMBOL_ICONS, recognisedSymbolOf, shapeDescriptorsOf, type SymbolKind,
} from './symbol-shapes';
import {
  DEFAULT_TRACE_CONFIG, traceConfigOf, type TraceConfig, type TraceConfigOverride,
} from './trace-config';

/**
 * Reading a floorplan off an image, in the browser: the image never leaves it.
 *
 * Works from the drawing's lines, the way a person reads a plan:
 *
 * - The ink is separated from the paper against its own surroundings, so a photo with a shadow
 *   across it or a pencil sketch reads as well as a clean print, and grey room fills are paper.
 * - A photo taken at a slight angle, or a crooked scan, is turned square first.
 * - A sheet frame round the drawing is recognised and left out.
 * - The walls — every long run of ink joined to the building — are told apart from what stands
 *   free on the floor: desks, tables, text.
 * - Door openings are closed across so each room is a room of its own, and the door swing drawn
 *   in the opening is kept as a door. Its size gives the plan its scale: a door is 0.85 m.
 * - Every patch of floor the walls close off is a room, traced to its real shape.
 * - A closed rectangle the size of a desk is a desk, turned the way it is drawn and as big as it
 *   is drawn. A run of narrow steps is a staircase; a thin strip in an outside wall a window.
 * - Walls drawn thick are kept as walls with their thickness; a free-standing partition is kept
 *   whatever its thickness. Walls at an angle are found too: every direction the drawing's lines
 *   run in is read turned level, doorways in it closed and walls read off it the same way. The
 *   building's outline is its real outline, not a box round it.
 * - The symbols drawn for fittings — a toilet, a washbasin, a bath, a shower tray, a hob — are
 *   recognised by their shape (`symbol-shapes`) and kept as those, with their icons.
 * - Anything else standing free on the floor — tables, sofas, counters, columns — is kept as
 *   furniture, in its drawn shape.
 *
 * What it does not do is read text: rooms arrive unnamed and written words are left out. The
 * portal says what was found, and everything is ordinary, editable geometry.
 *
 * Pure over a grid of pixels, so it is tested without a canvas; the portal hands it the image.
 */

/** A grey image: one byte per pixel, 0 black to 255 white, row by row. */
export interface GreyImage {
  readonly width: number;
  readonly height: number;
  readonly pixels: Uint8ClampedArray | Uint8Array;
}

/**
 * The thresholds the tracer is reading by: the defaults, or what `traceSketch` was asked to read
 * with. Set for the length of one call to it, which runs to its end before anything else can.
 */
let active: TraceConfig = DEFAULT_TRACE_CONFIG;

/**
 * The steps of a trace, in order: squaring the picture up, splitting its ink into parts, finding
 * the directions its lines run in, and then each step of a reading, which happens twice when the
 * doors say how wide a doorway really is.
 */
export type TraceStage = 'straighten' | 'parts' | 'directions'
  | 'spaces' | 'building' | 'doors' | 'objects' | 'rooms' | 'walls' | 'writing' | 'furniture' | 'openings';

/** Told as each step starts: for a progress bar, and for timing the steps. */
export type StageListener = (stage: TraceStage, reading: 1 | 2) => void;

/** Who is told of the steps of the trace running now, and which reading it is on. */
let listener: StageListener | undefined;
let reading: 1 | 2 = 1;
const starting = (stage: TraceStage): void => listener?.(stage, reading);

/**
 * The grey level that best splits ink from paper, by Otsu's method: the threshold that makes the
 * two groups as different from each other as possible.
 */
export const otsuThreshold = (pixels: Uint8ClampedArray | Uint8Array): number => {
  const histogram = new Array<number>(256).fill(0);
  for (let index = 0; index < pixels.length; index += 1) histogram[pixels[index]] += 1;

  const total = pixels.length;
  const sumAll = histogram.reduce((sum, count, level) => sum + (count * level), 0);
  let sumBackground = 0;
  let weightBackground = 0;
  let best = 0;
  let threshold = 127;

  for (let level = 0; level < 256; level += 1) {
    weightBackground += histogram[level];
    sumBackground += level * histogram[level];
    const weightForeground = total - weightBackground;

    if (weightBackground > 0 && weightForeground > 0) {
      const meanBackground = sumBackground / weightBackground;
      const meanForeground = (sumAll - sumBackground) / weightForeground;
      const between = weightBackground * weightForeground * (meanBackground - meanForeground) ** 2;

      if (between > best) {
        best = between;
        threshold = level;
      }
    }
  }

  return threshold;
};

/** The directions across a line a faint pixel is looked either side of, and how far at most. */
const LINE_STEPS: readonly Pixel[] = [[1, 0], [0, 1], [1, 1], [1, -1]];
const LINE_REACH = 6;

/**
 * Ink as 1 and paper as 0.
 *
 * A pixel is ink when it is clearly darker than the paper around it — which is what survives a
 * shadow across a photo, where no single grey level splits ink from paper everywhere — or when it
 * is dark overall and sits in dark surroundings, which is the inside of a wall drawn solid. A
 * light grey room fill is darker than white paper but not than its surroundings, so it stays
 * paper. Specks too small to be any part of a drawing are dropped.
 */
export const inkOf = (image: GreyImage, config: TraceConfigOverride = {}): Uint8Array => {
  const { localContrast } = traceConfigOf(config).ink;
  const { width, height, pixels } = image;
  const global = otsuThreshold(pixels);
  const stride = width + 1;
  const integral = new Float64Array(stride * (height + 1));
  // The paper alone — what is lighter than the ink split — summed and counted too.
  const paperSum = new Float64Array(stride * (height + 1));
  const paperCount = new Float64Array(stride * (height + 1));

  for (let y = 0; y < height; y += 1) {
    let row = 0;
    let rowPaper = 0;
    let rowPaperCount = 0;

    for (let x = 0; x < width; x += 1) {
      const value = pixels[(y * width) + x];
      row += value;
      if (value > global) {
        rowPaper += value;
        rowPaperCount += 1;
      }
      const at = ((y + 1) * stride) + x + 1;
      const above = (y * stride) + x + 1;
      integral[at] = integral[above] + row;
      paperSum[at] = paperSum[above] + rowPaper;
      paperCount[at] = paperCount[above] + rowPaperCount;
    }
  }

  const half = Math.max(7, Math.round(Math.min(width, height) / 16));
  const ink = new Uint8Array(width * height);

  // Whether the pixel is a line's: lighter on both sides of it, a little way off along some
  // direction — across the line, whichever way it runs — with nothing darker on the way. The
  // inside of a flat grey fill never is, nor the fill in a corner of the walls round it.
  const isLineAt = (x: number, y: number, value: number): boolean => {
    const [brighter, darker] = [value * (1 + (localContrast / 3)), value * (1 - localContrast)];
    return LINE_STEPS.some(([dx, dy]) => {
      for (let reach = 1; reach <= LINE_REACH; reach += 1) {
        const [ax, ay, bx, by] = [x - (dx * reach), y - (dy * reach), x + (dx * reach), y + (dy * reach)];
        if (ax < 0 || bx < 0 || ax >= width || bx >= width || ay < 0 || by < 0 || ay >= height || by >= height) return false;
        const [before, after] = [pixels[(ay * width) + ax], pixels[(by * width) + bx]];
        if (before < darker || after < darker) return false;
        if (reach > 1 && before > brighter && after > brighter) return true;
      }
      return false;
    });
  };

  for (let y = 0; y < height; y += 1) {
    const top = Math.max(0, y - half);
    const bottom = Math.min(height - 1, y + half);

    for (let x = 0; x < width; x += 1) {
      const left = Math.max(0, x - half);
      const right = Math.min(width - 1, x + half);
      // The window's four corners in the running sums.
      const [a, b] = [((bottom + 1) * stride) + right + 1, (top * stride) + right + 1];
      const [c, d] = [((bottom + 1) * stride) + left, (top * stride) + left];
      const mean = (integral[a] - integral[b] - integral[c] + integral[d]) / ((bottom - top + 1) * (right - left + 1));
      const value = pixels[(y * width) + x];

      let isInk = value < mean * (1 - localContrast) || (value <= global && mean <= global);
      if (!isInk && value < mean) {
        // Darker than its surroundings, and clearly darker than the paper round it: a faint line
        // where much else is drawn — a blurred photo's crowded corner — pulls the mean down. And
        // a line, lighter on both sides of it: beside a wall, white paper and a grey room fill
        // share a window, and the fill is darker than that paper without being ink.
        const papers = paperCount[a] - paperCount[b] - paperCount[c] + paperCount[d];
        const paperMean = papers > 0 ? (paperSum[a] - paperSum[b] - paperSum[c] + paperSum[d]) / papers : mean;
        isInk = value < paperMean * (1 - localContrast) && isLineAt(x, y, value);
      }
      ink[(y * width) + x] = isInk ? 1 : 0;
    }
  }

  const speck = Math.max(3, Math.round((width * height) / 250000));
  const { labels, regions } = labelRegions({ width, height, data: ink }, 1, true);

  const tooSmall = Uint8Array.from(regions, (region) => (region.area < speck ? 1 : 0));
  for (let index = 0; index < labels.length; index += 1) {
    const label = labels[index];
    if (label >= 0 && tooSmall[label]) ink[index] = 0;
  }

  return ink;
};

/**
 * Openings in walls closed across: along every row and every column, a gap of paper is filled
 * when the ink on at least one side of it is a wall — a run at least `minRun` long — pointing at
 * it. A gap with wall in line on both sides is a doorway up to `maxGap`; one with wall on only one
 * side is closed up to a little less, so a corridor's mouth stays open. That closes doorways, and
 * the corner a quick sketch did not quite join, without joining up the strokes of a letter or two
 * desks side by side. `alongRowsOnly` leaves the columns: for a picture turned so one direction
 * of walls lies level, when no walls are drawn square to it. `oneSidedShare` is how much of a
 * doorway a gap with wall on one side only is closed up to: the thresholds' own unless given.
 */
export const bridged = (
  mask: Bitmap,
  maxGap: number,
  minRun: number,
  alongRowsOnly = false,
  oneSidedShare = active.openings.oneSidedShare,
): Bitmap => {
  const { width, height, data } = mask;
  const out = data.slice();

  // Along each line — a row, stepping 1, or a column, stepping a row's width — from its start.
  const pass = (lines: number, length: number, startOf: (line: number) => number, step: number): void => {
    for (let line = 0; line < lines; line += 1) {
      const first = startOf(line);
      let previousEnd = -1;
      let previousLength = 0;
      let start = -1;

      for (let position = 0; position <= length; position += 1) {
        const isInk = position < length && data[first + (position * step)] === 1;

        if (isInk && start < 0) {
          start = position;
        } else if (!isInk && start >= 0) {
          const end = position - 1;
          const gap = start - previousEnd - 1;

          const bothWalls = previousLength >= minRun && (end - start) + 1 >= minRun;
          const oneWall = previousLength >= minRun || (end - start) + 1 >= minRun;
          const limit = bothWalls ? maxGap : maxGap * oneSidedShare;

          if (previousEnd >= 0 && gap > 0 && gap <= limit && oneWall) {
            for (let fill = previousEnd + 1; fill < start; fill += 1) {
              out[first + (fill * step)] = 1;
            }
          }

          previousEnd = end;
          previousLength = (end - start) + 1;
          start = -1;
        }
      }
    }
  };

  pass(height, width, (line) => line * width, 1);
  if (!alongRowsOnly) pass(width, height, (line) => line, width);
  return { width, height, data: out };
};

/** A straight run of wall: the rows (or columns) it spans and where along them it starts and ends. */
interface Bar {
  readonly horizontal: boolean;
  readonly start: number;
  readonly end: number;
  readonly first: number;
  readonly last: number;
}

interface BarStack {
  readonly starts: number[];
  readonly ends: number[];
  readonly first: number;
  last: number;
}

/**
 * The straight walls in `mask`, running along the rows (`horizontal`) or the columns: every run at
 * least `minLength` long, stacked with the runs beside it into one bar as thick as the stack.
 */
const barsOf = (mask: Bitmap, horizontal: boolean, minLength: number): Bar[] => {
  const { width, height, data } = mask;
  const lines = horizontal ? height : width;
  const length = horizontal ? width : height;
  const at = (line: number, position: number): number => (horizontal ? (line * width) + position : (position * width) + line);

  const { barOverlap, barLengthRatio } = active.walls;
  let open: BarStack[] = [];
  const done: BarStack[] = [];

  for (let line = 0; line < lines; line += 1) {
    const runs: Pixel[] = [];
    let start = -1;

    for (let position = 0; position <= length; position += 1) {
      const isInk = position < length && data[at(line, position)] === 1;

      if (isInk && start < 0) {
        start = position;
      } else if (!isInk && start >= 0) {
        if (position - start >= minLength) runs.push([start, position - 1]);
        start = -1;
      }
    }

    const continued: BarStack[] = [];
    const previous = open;

    runs.forEach(([runStart, runEnd]) => {
      // Measured against the stack's last run, so a stack follows a line aslant down the rows,
      // one run a little along from the next, into a stack too thick for its length to be a
      // wall: which is how the level read leaves lines aslant to the turned reads. Measured
      // against its longest or middle run instead, a line aslant breaks into short stacks, each
      // passing for a stub of wall along the rows.
      const match = previous.find((stack) => {
        const lastStart = stack.starts[stack.starts.length - 1];
        const lastEnd = stack.ends[stack.ends.length - 1];
        const overlap = Math.min(runEnd, lastEnd) - Math.max(runStart, lastStart) + 1;
        const [shorter, longer] = [runEnd - runStart + 1, lastEnd - lastStart + 1].sort((a, b) => a - b);
        // Much longer is another wall: a wall square to the rows joining one crossing them aslant.
        return !continued.includes(stack) && overlap >= barOverlap * shorter && longer <= barLengthRatio * shorter;
      });

      if (match) {
        match.starts.push(runStart);
        match.ends.push(runEnd);
        match.last = line;
        continued.push(match);
      } else {
        continued.push({
          starts: [runStart], ends: [runEnd], first: line, last: line,
        });
      }
    });

    previous.filter((stack) => !continued.includes(stack)).forEach((stack) => done.push(stack));
    open = continued;
  }

  return [...done, ...open].map((stack) => ({
    horizontal,
    start: medianOf(stack.starts),
    end: medianOf(stack.ends),
    first: stack.first,
    last: stack.last,
  }));
};

interface Door {
  readonly region: Region;
  readonly hinge: Pixel;
  /** Which way the swing opens from the hinge, along each axis: 1 or -1. */
  readonly opens: Pixel;
  /** From the hinge towards the middle of the swing. */
  readonly towards: Pixel;
  readonly radius: number;
  /**
   * For a door drawn at an angle: the two directions, on the picture, its quarter circle spans
   * from the hinge — where for a level door they are `opens` along each axis.
   */
  readonly arms?: readonly [Pixel, Pixel];
}

/** Corners in a door swing's quarter circle: enough to read as a curve at any zoom. */
const DOOR_ARC_STEPS = 12;

/**
 * A door swing: a quarter circle with its corner at the hinge. Told from a rectangle and from a
 * room with a corner cut off by what lies along its diagonal: from the hinge, the swing is still
 * there 0.6 of the way to the far corner and gone by 0.82, and the far corner itself is empty.
 */
const doorOf = (labels: Int32Array, width: number, region: Region, minDim: number, stroke: number): Door | undefined => {
  const boxWidth = boxWidthOf(region);
  const boxHeight = boxHeightOf(region);
  const size = (boxWidth + boxHeight) / 2;
  const fill = region.area / (boxWidth * boxHeight);

  if (
    region.touchesEdge
    || Math.max(boxWidth, boxHeight) > Math.min(boxWidth, boxHeight) * active.doors.aspect
    || fill < active.doors.fill.min || fill > active.doors.fill.max
    || size < minDim * active.doors.size.min || size > minDim * active.doors.size.max
  ) {
    return undefined;
  }

  const inside = (fx: number, fy: number): boolean => {
    const x = Math.round(region.minX + (fx * (boxWidth - 1)));
    const y = Math.round(region.minY + (fy * (boxHeight - 1)));
    return labels[(y * width) + x] === region.label;
  };

  // The four corners of the box, each with the way into the box from it.
  const corners: { corner: Pixel; toward: Pixel; at: Pixel }[] = [
    { corner: [0, 0], toward: [1, 1], at: [region.minX, region.minY] },
    { corner: [1, 0], toward: [-1, 1], at: [region.maxX + 1, region.minY] },
    { corner: [1, 1], toward: [-1, -1], at: [region.maxX + 1, region.maxY + 1] },
    { corner: [0, 1], toward: [1, -1], at: [region.minX, region.maxY + 1] },
  ];

  const hinge = corners.find(({ corner, toward }) => {
    const along = (fraction: number): boolean => inside(corner[0] + (toward[0] * fraction), corner[1] + (toward[1] * fraction));
    return along(0.1) && along(0.6) && !along(0.82) && !along(0.97);
  });

  if (!hinge) {
    return undefined;
  }

  return {
    region,
    // The hinge is on the middle of the lines, a pixel beyond where the swing's paper stops.
    hinge: [hinge.at[0] - hinge.toward[0], hinge.at[1] - hinge.toward[1]],
    opens: hinge.toward,
    towards: [hinge.toward[0] / Math.SQRT2, hinge.toward[1] / Math.SQRT2],
    // The swing's paper stops at the inside of the leaf and the arc, both thickened a pixel to
    // close gaps; the door reaches most of a (thickened) stroke further.
    radius: size + (stroke * 0.75),
  };
};

/**
 * `doorOf` for a door drawn at `degrees`: the same test, on the patch measured along that
 * direction and the one square to it rather than along rows and columns.
 */
const turnedDoorOf = (
  labels: Int32Array,
  width: number,
  region: Region,
  degrees: number,
  minDim: number,
  stroke: number,
): Door | undefined => {
  if (region.touchesEdge) return undefined;
  const radians = (degrees * Math.PI) / 180;
  const [cos, sin] = [Math.cos(radians), Math.sin(radians)];
  let [minU, maxU, minV, maxV] = [Infinity, -Infinity, Infinity, -Infinity];

  for (let y = region.minY; y <= region.maxY; y += 1) {
    for (let x = region.minX; x <= region.maxX; x += 1) {
      if (labels[(y * width) + x] === region.label) {
        const u = (x * cos) + (y * sin);
        const v = (y * cos) - (x * sin);
        if (u < minU) minU = u;
        if (u > maxU) maxU = u;
        if (v < minV) minV = v;
        if (v > maxV) maxV = v;
      }
    }
  }

  const boxWidth = (maxU - minU) + 1;
  const boxHeight = (maxV - minV) + 1;
  const size = (boxWidth + boxHeight) / 2;
  const fill = region.area / (boxWidth * boxHeight);

  if (
    Math.max(boxWidth, boxHeight) > Math.min(boxWidth, boxHeight) * active.doors.aspect
    || fill < active.doors.fill.min || fill > active.doors.fill.max
    || size < minDim * active.doors.size.min || size > minDim * active.doors.size.max
  ) {
    return undefined;
  }

  const toPicture = (u: number, v: number): Pixel => [(u * cos) - (v * sin), (u * sin) + (v * cos)];
  const inside = (fu: number, fv: number): boolean => {
    const [x, y] = toPicture(minU + (fu * (boxWidth - 1)), minV + (fv * (boxHeight - 1)));
    const [px, py] = [Math.round(x), Math.round(y)];
    return px >= 0 && py >= 0 && px < width && labels[(py * width) + px] === region.label;
  };

  // The box's corners, on the pixels' edges as `doorOf`'s are: `u` and `v` are of the pixels'
  // top-left corners, and a pixel's middle is half a pixel on from its corner each way, turned.
  const [middleU, middleV] = [(cos + sin) / 2, (cos - sin) / 2];
  const [left, right] = [minU + middleU - 0.5, maxU + middleU + 0.5];
  const [top, bottom] = [minV + middleV - 0.5, maxV + middleV + 0.5];
  const corners: { corner: Pixel; toward: Pixel; at: Pixel }[] = [
    { corner: [0, 0], toward: [1, 1], at: [left, top] },
    { corner: [1, 0], toward: [-1, 1], at: [right, top] },
    { corner: [1, 1], toward: [-1, -1], at: [right, bottom] },
    { corner: [0, 1], toward: [1, -1], at: [left, bottom] },
  ];

  const hinge = corners.find(({ corner, toward }) => {
    const along = (fraction: number): boolean => inside(corner[0] + (toward[0] * fraction), corner[1] + (toward[1] * fraction));
    return along(0.1) && along(0.6) && !along(0.82) && !along(0.97);
  });

  if (!hinge) return undefined;

  const [su, sv] = hinge.toward;
  const along: Pixel = [su * cos, su * sin];
  const across: Pixel = [-sv * sin, sv * cos];
  return {
    region,
    hinge: toPicture(hinge.at[0] - su, hinge.at[1] - sv),
    opens: hinge.toward,
    towards: [(along[0] + across[0]) / Math.SQRT2, (along[1] + across[1]) / Math.SQRT2],
    radius: size + (stroke * 0.75),
    arms: [along, across],
  };
};

/** Which kind of small closed shape a patch of paper is, by its turned box alone. */
type ShapeKind = 'desk' | 'tread' | 'strip' | 'other';

const shapeKindOf = (box: OrientedBox, area: number, buildingArea: number): ShapeKind => {
  const aspect = box.length / Math.max(1, box.breadth);

  if (box.fill >= 0.75 && aspect >= active.windows.aspect) return 'strip';
  if (box.fill >= active.desks.fill && aspect >= active.stairs.treadAspect && area <= buildingArea * active.desks.share.max) return 'tread';

  if (
    box.fill >= active.desks.fill
    && aspect >= active.desks.aspect.min && aspect <= active.desks.aspect.max
    && area >= buildingArea * active.desks.share.min && area <= buildingArea * active.desks.share.max
  ) {
    return 'desk';
  }

  return 'other';
};

/** A plan's angle for a direction drawn on the image: the image counts down, the plan up. */
const planDegreesOf = (imageDegrees: number): number => {
  let degrees = -imageDegrees;
  while (degrees <= -90) degrees += 180;
  while (degrees > 90) degrees -= 180;

  if (Math.abs(degrees) < 3) return 0;
  if (Math.abs(Math.abs(degrees) - 90) < 3) return 90;
  return Math.round(degrees * 10) / 10;
};

const cornersOf = (box: OrientedBox, grow = 0): Pixel[] => {
  const radians = (box.angleDegrees * Math.PI) / 180;
  const [ux, uy] = [Math.cos(radians), Math.sin(radians)];
  const [vx, vy] = [-uy, ux];
  const halfLength = (box.length / 2) + grow;
  const halfBreadth = (box.breadth / 2) + grow;
  const [cx, cy] = box.centre;

  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([su, sv]): Pixel => [
    cx + (su * halfLength * ux) + (sv * halfBreadth * vx),
    cy + (su * halfLength * uy) + (sv * halfBreadth * vy),
  ]);
};

/** The difference between two directions, ignoring which way along them: 0 to 90 degrees. */
const turnBetween = (a: number, b: number): number => {
  const turn = (((a - b) % 180) + 180) % 180;
  return Math.min(turn, 180 - turn);
};

/**
 * Which strips of glass belong to one window: a window drawn as three lines is two strips side by
 * side, lying the same way, overlapping along their length and a line's width apart. Returns the
 * indexes of `boxes`, grouped.
 */
const panesTogether = (boxes: readonly OrientedBox[], stroke: number): number[][] => {
  const windowOf = boxes.map((_, index) => index);
  const root = (index: number): number => (windowOf[index] === index ? index : root(windowOf[index]));

  boxes.forEach((a, i) => {
    boxes.forEach((b, j) => {
      if (j <= i || turnBetween(a.angleDegrees, b.angleDegrees) > 5) return;
      const radians = (a.angleDegrees * Math.PI) / 180;
      const dx = b.centre[0] - a.centre[0];
      const dy = b.centre[1] - a.centre[1];
      const along = Math.abs((dx * Math.cos(radians)) + (dy * Math.sin(radians)));
      const across = Math.abs((dy * Math.cos(radians)) - (dx * Math.sin(radians)));
      const overlap = ((a.length + b.length) / 2) - along;

      if (overlap >= Math.min(a.length, b.length) * 0.5 && across <= a.breadth + b.breadth + (3 * stroke) + 2) {
        windowOf[root(j)] = root(i);
      }
    });
  });

  const groups = new Map<number, number[]>();
  boxes.forEach((_, index) => {
    const key = root(index);
    groups.set(key, [...(groups.get(key) ?? []), index]);
  });
  return Array.from(groups.values());
};

/** One window's outline from its strips of glass: the strip itself, or the box round them all. */
const windowOutlineOf = (boxes: readonly OrientedBox[], grow: number): Pixel[] => {
  if (boxes.length === 1) return cornersOf(boxes[0], grow);

  const corners = boxes.flatMap((box) => cornersOf(box, grow));
  const xs = corners.map(([x]) => x);
  const ys = corners.map(([, y]) => y);
  const [left, top, right, bottom] = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  return [[left, top], [right, top], [right, bottom], [left, bottom]];
};

/** What one reading of the drawing found, and the sizes a second reading can be tuned by. */
interface Reading {
  readonly sketch: FloorPlanSketch;
  readonly doorWidths: number[];
  readonly deskLengths: number[];
  /** The drawing's ink, to keep what is not replaced as drawn: made only for the reading kept. */
  readonly ink: () => SketchInk;
}

/** The drawing as it is read: squared up, its lines thickened, split into connected parts. */
interface Drawing {
  readonly frame: Bitmap;
  readonly parts: Labelled;
  /** Per part: false for a sheet frame round the drawing. */
  readonly kept: readonly boolean[];
  readonly extent: { minX: number; maxX: number; minY: number; maxY: number };
  /** How wide a line is drawn, thickened. */
  readonly stroke: number;
  /** The directions other than level and plumb that the drawing's lines run in. */
  readonly directions: readonly LineDirection[];
  readonly aspect: number | null;
  readonly turn: SketchFrame;
}

/** The pictures of chosen parts already made for a drawing, by which parts. */
const drawnMasks = new WeakMap<Drawing, Map<string, Bitmap>>();
const masksOf = (drawing: Drawing): Map<string, Bitmap> => {
  let masks = drawnMasks.get(drawing);
  if (!masks) {
    masks = new Map();
    drawnMasks.set(drawing, masks);
  }
  return masks;
};

/**
 * What is worked out from a mask of the structure alone, kept with the mask, so the second
 * reading — whose mask is the same one unless the structure changed — and the steps of one
 * reading that turn it the same way do not work it out again: the mask turned to each angle, and
 * the straight bars along it.
 */
interface MaskWork {
  readonly turned: Map<number, Bitmap>;
  readonly bars: Map<string, Bar[]>;
}

const maskWork = new WeakMap<Bitmap, MaskWork>();
const workOf = (mask: Bitmap): MaskWork => {
  let work = maskWork.get(mask);
  if (!work) {
    work = { turned: new Map(), bars: new Map() };
    maskWork.set(mask, work);
  }
  return work;
};

/** `rotateBitmap(mask, degrees)`, made once per mask and angle. Never to be changed. */
const turnedMask = (mask: Bitmap, degrees: number): Bitmap => {
  const { turned } = workOf(mask);
  let bitmap = turned.get(degrees);
  if (!bitmap) {
    bitmap = rotateBitmap(mask, degrees);
    turned.set(degrees, bitmap);
  }
  return bitmap;
};

/** `barsOf(mask, horizontal, minLength)`, found once per mask. */
const barsAlong = (mask: Bitmap, horizontal: boolean, minLength: number): Bar[] => {
  const { bars } = workOf(mask);
  const key = `${horizontal}:${minLength}:${active.walls.barOverlap}:${active.walls.barLengthRatio}`;
  let found = bars.get(key);
  if (!found) {
    found = barsOf(mask, horizontal, minLength);
    bars.set(key, found);
  }
  return found;
};

/** What every step of a reading shares: the drawing, its sizes, and the way to sketch coordinates. */
interface Page {
  readonly drawing: Drawing;
  readonly width: number;
  readonly height: number;
  /** The connected part of the (thickened) ink each pixel belongs to, or -1 for paper. */
  readonly partOf: Int32Array;
  readonly extentWidth: number;
  readonly extentHeight: number;
  readonly minDim: number;
  readonly stroke: number;
  readonly normalise: (x: number, y: number) => SketchPoint;
  readonly lengthOf: (pixels: number) => number;
  readonly outlineOf: (points: readonly Pixel[]) => SketchPoint[];
  /** The ink of the parts `include` chooses. Shared between readings, so never to be changed. */
  readonly maskOf: (include: (label: number) => boolean) => Bitmap;
}

/** The building's structure, and the spaces it closes off. */
interface Spaces {
  /** Per part: whether it is walls rather than something standing on the floor. */
  readonly structural: boolean[];
  /** The structure with its openings closed across. */
  readonly walls: Bitmap;
  /**
   * What closing the openings filled in, at its real size: `walls` has what was closed aslant
   * thickened a pixel, so no crack is left between it and the wall it closes.
   */
  readonly filled: Bitmap;
  /** The patches of paper the closed structure leaves. */
  readonly paper: Labelled;
  readonly isOutside: (x: number, y: number) => boolean;
}

interface Building {
  readonly region: Region;
  readonly area: number;
  readonly inBuilding: (pixel: Pixel) => boolean;
  readonly border: Pixel[];
}

/** A small closed patch of paper that might be a desk, a step, a window or a cupboard. */
interface Candidate {
  readonly region: Region;
  /** Its top-left pixel: paper for a patch of paper, ink for a desk drawn filled in. */
  readonly at: Pixel;
  readonly box: OrientedBox;
  readonly kind: ShapeKind;
  /** The part of the ink it lies inside. */
  readonly host: number;
  /** Drawn filled in, rather than as an outline round paper. */
  readonly solid?: boolean;
}

/**
 * How long and deep a desk really is. A patch of paper stops at the inside of the outline drawn
 * round it, a stroke short; a desk drawn filled in is its ink, thickened a pixel each side.
 */
const drawnSizeOf = (candidate: Candidate, stroke: number): { length: number; depth: number } => (candidate.solid
  ? { length: Math.max(1, candidate.box.length - 2), depth: Math.max(1, candidate.box.breadth - 2) }
  : { length: candidate.box.length + stroke, depth: candidate.box.breadth + stroke });

const clampUnit = (value: number): number => Math.round(Math.min(1, Math.max(0, value)) * 100) / 100;

/**
 * How sure a desk is: how cleanly rectangular it is, and how close to the plan's usual desk.
 * A plan's desks are mostly one size, so the odd one out is the one worth a second look.
 */
const deskConfidenceOf = (candidate: Candidate, typicalLength: number, stroke: number): number => {
  // With desks asked to fill their whole box, one that does is as square as a desk gets.
  const room = 1 - active.desks.fill;
  const squareness = room > 0 ? Math.min(1, Math.max(0, (candidate.box.fill - active.desks.fill) / room)) : Number(candidate.box.fill >= 1);
  const { length } = drawnSizeOf(candidate, stroke);
  const likeTheOthers = typicalLength > 0 ? 1 - Math.min(1, Math.abs((length / typicalLength) - 1) / 0.4) : 0.5;
  return clampUnit(0.45 + (0.25 * squareness) + (0.3 * likeTheOthers));
};

/** A fitting's symbol: the part of the ink it is drawn in, what lies inside it, and what it is. */
interface FoundSymbol {
  readonly part: Region;
  readonly members: readonly number[];
  readonly kind: SymbolKind;
  readonly confidence: number;
}

/** What stands on the floor, as far as the small closed shapes tell. */
interface Objects {
  readonly islands: Region[];
  readonly islandLabels: ReadonlySet<number>;
  readonly candidates: Candidate[];
  readonly stairs: Candidate[][];
  readonly stairOutlines: ReadonlySet<number>;
  readonly windows: Candidate[];
  readonly desks: Candidate[];
  readonly islandsWithDesks: ReadonlySet<number>;
  readonly metresPerPixelFromDoors: number | null;
  /** The fittings' symbols found standing free, and every part of the ink they are drawn with. */
  readonly symbols: readonly FoundSymbol[];
  readonly inSymbols: ReadonlySet<number>;
}

/**
 * The fittings among the shapes standing free: each part of the ink, with the parts drawn inside
 * its loops (a washbasin's basin, a hob's rings), described and matched against the symbols'
 * templates. A part inside another is read with it, not on its own.
 */
const symbolsAmong = (page: Page, islands: readonly Region[], metresPerPixel: number | null): FoundSymbol[] => {
  const {
    width, partOf, minDim, stroke,
  } = page;
  const smallest = minDim * active.marks.markShare;
  const largest = minDim * active.symbols.maxShare;
  const sized = islands.filter((part) => Math.max(boxWidthOf(part), boxHeightOf(part)) > smallest
    && Math.max(boxWidthOf(part), boxHeightOf(part)) <= largest);

  // Which parts lie in the loops of which: a part's own loops are the paper it closes off.
  const insideOf = new Map<number, number[]>();
  const nestedSomewhere = new Set<number>();

  sized.forEach((host) => {
    const [cropWidth, cropHeight] = [boxWidthOf(host) + 2, boxHeightOf(host) + 2];
    const [left, top] = [host.minX - 1, host.minY - 1];
    const own = new Uint8Array(cropWidth * cropHeight);
    for (let y = host.minY; y <= host.maxY; y += 1) {
      for (let x = host.minX; x <= host.maxX; x += 1) {
        if (partOf[(y * width) + x] === host.label) own[((y - top) * cropWidth) + (x - left)] = 1;
      }
    }
    const paper = labelRegions({ width: cropWidth, height: cropHeight, data: own }, 0, false);

    islands.forEach((other) => {
      const within = other.minX > host.minX && other.maxX < host.maxX && other.minY > host.minY && other.maxY < host.maxY;
      if (other === host || !within) return;
      const [x, y] = topLeftOf(partOf, width, other);
      const label = paper.labels[((y - top) * cropWidth) + (x - left)];
      if (label >= 0 && !paper.regions[label].touchesEdge) {
        insideOf.set(host.label, [...(insideOf.get(host.label) ?? []), other.label]);
        nestedSomewhere.add(other.label);
      }
    });
  });

  const symbolOf = (part: Region): FoundSymbol | null => {
    const members = [part.label, ...(insideOf.get(part.label) ?? [])];
    const [cropWidth, cropHeight] = [boxWidthOf(part) + 2, boxHeightOf(part) + 2];
    const [left, top] = [part.minX - 1, part.minY - 1];
    const ink = new Uint8Array(cropWidth * cropHeight);
    for (let y = part.minY; y <= part.maxY; y += 1) {
      for (let x = part.minX; x <= part.maxX; x += 1) {
        if (members.includes(partOf[(y * width) + x])) ink[((y - top) * cropWidth) + (x - left)] = 1;
      }
    }

    const shape = shapeDescriptorsOf({ width: cropWidth, height: cropHeight, data: ink }, stroke, metresPerPixel);
    const symbol = shape && recognisedSymbolOf(shape);
    return symbol && symbol.confidence >= active.symbols.minConfidence ? { part, members, ...symbol } : null;
  };

  // A part that is no fitting may have fittings drawn inside it — a sink in a kitchen counter —
  // so what lies directly inside it is read too, each with what lies inside that.
  const bySize = new Map(sized.map((part) => [part.label, part]));
  const symbolsIn = (part: Region): FoundSymbol[] => {
    const symbol = symbolOf(part);
    if (symbol) return [symbol];
    const inside = insideOf.get(part.label) ?? [];
    return inside
      .filter((label) => !inside.some((other) => other !== label && (insideOf.get(other) ?? []).includes(label)))
      .map((label) => bySize.get(label))
      .filter((member): member is Region => member !== undefined)
      .flatMap(symbolsIn);
  };

  return sized.filter((part) => !nestedSomewhere.has(part.label)).flatMap(symbolsIn);
};

const pageOf = (drawing: Drawing): Page => {
  const { width, height } = drawing.frame;
  const partOf = drawing.parts.labels;
  const extentWidth = drawing.extent.maxX - drawing.extent.minX + 1;
  const extentHeight = drawing.extent.maxY - drawing.extent.minY + 1;
  const normalise = (x: number, y: number): SketchPoint => ({
    x: Math.round((x / width) * SKETCH_EXTENT * 100) / 100,
    y: Math.round((y / height) * SKETCH_EXTENT * 100) / 100,
  });

  return {
    drawing,
    width,
    height,
    partOf,
    extentWidth,
    extentHeight,
    minDim: Math.min(extentWidth, extentHeight),
    stroke: drawing.stroke,
    normalise,
    lengthOf: (pixels) => Math.round((pixels / width) * SKETCH_EXTENT * 100) / 100,
    outlineOf: (points) => points.map(([x, y]) => normalise(x, y)),
    maskOf: (include): Bitmap => {
      // Which parts are in, as a table, so the picture is gone through without a call a pixel;
      // and the same parts' picture is kept, so a second reading does not make it again.
      const chosen = Uint8Array.from(drawing.parts.regions, (region) => (include(region.label) ? 1 : 0));
      const key = chosen.join('');
      const masks = masksOf(drawing);
      const kept = masks.get(key);
      if (kept) return kept;

      const data = new Uint8Array(width * height);
      for (let index = 0; index < data.length; index += 1) {
        const label = partOf[index];
        if (label >= 0 && chosen[label]) data[index] = 1;
      }
      const mask = { width, height, data };
      masks.set(key, mask);
      return mask;
    },
  };
};

/** A direction to read walls along, and whether the one square to it is also drawn. */
interface DirectionGroup {
  readonly degrees: number;
  readonly squareToo: boolean;
}

/** The drawing's angled directions, each paired with the one square to it when both are drawn. */
const directionGroupsOf = (directions: readonly LineDirection[]): DirectionGroup[] => {
  const pending = [...directions];
  const groups: DirectionGroup[] = [];

  while (pending.length) {
    const direction = pending.shift() as LineDirection;
    const squareTo = pending.findIndex((other) => Math.abs(Math.abs(other.degrees - direction.degrees) - 90) <= 3);
    if (squareTo >= 0) pending.splice(squareTo, 1);
    groups.push({ degrees: direction.degrees, squareToo: squareTo >= 0 });
  }

  return groups;
};

/**
 * `bridged` along every angled direction too: the structure turned so the direction lies level,
 * its openings closed across there, and what was closed put back on the picture — thickened a
 * pixel, so turning it back leaves no crack between it and the wall it closes.
 */
const bridgedAtAngles = (
  structure: Bitmap,
  groups: readonly DirectionGroup[],
  maxGap: number,
  minRun: number,
  oneSidedShare: number,
): { walls: Bitmap; filled: Bitmap } => {
  let walls = bridged(structure, maxGap, minRun, false, oneSidedShare);
  const level = walls;
  const { width, height } = structure;
  // What was closed across along each angled direction, as closed and not thickened.
  const aslant = new Uint8Array(width * height);

  groups.forEach(({ degrees, squareToo }) => {
    const turn = turnOf(width, height, -degrees);
    const turned = turnedMask(structure, -degrees);
    // Across the direction only when walls are drawn that way too: otherwise what the columns
    // would close is the corner between this direction and another.
    const closedThere = bridged(turned, maxGap, minRun, !squareToo, oneSidedShare);
    const added = new Uint8Array(width * height);
    let any = false;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const [tx, ty] = turn.toTurned(x, y);
        const [rx, ry] = [Math.round(tx), Math.round(ty)];
        if (rx >= 0 && ry >= 0 && rx < turn.width && ry < turn.height) {
          const at = (ry * turn.width) + rx;
          if (closedThere.data[at] && !turned.data[at]) {
            added[(y * width) + x] = 1;
            aslant[(y * width) + x] = 1;
            any = true;
          }
        }
      }
    }

    if (any) {
      const grown = dilate({ width, height, data: added }, 1);
      const data = walls.data.slice();
      for (let index = 0; index < data.length; index += 1) if (grown.data[index]) data[index] = 1;
      walls = { width, height, data };
    }
  });

  // What was closed across, for the openings to be found in at their real size: along rows and
  // columns as filled; aslant as turned back, with the pixel-wide cracks turning back leaves in
  // it filled — a pixel with what was closed on both sides of it.
  const filled = new Uint8Array(width * height);
  for (let index = 0; index < filled.length; index += 1) {
    if (!structure.data[index] && walls.data[index]) {
      const x = index % width;
      const crack = !aslant[index] && x > 0 && x < width - 1 && index >= width && index < filled.length - width
        && ((aslant[index - 1] && aslant[index + 1]) || (aslant[index - width] && aslant[index + width]));
      filled[index] = level.data[index] || aslant[index] || crack ? 1 : 0;
    }
  }
  return { walls, filled: { width, height, data: filled } };
};

/** Step 1: which ink is the building's walls, and the spaces they close off with openings up to `maxGap`. */
const spacesOf = (page: Page, maxGap: number): Spaces => {
  const {
    drawing, width, height, partOf, extentWidth, extentHeight, minDim, stroke,
  } = page;
  const { parts, kept } = drawing;
  const minRun = Math.max(4 * stroke, minDim * active.openings.wallRunShare);

  const structural = parts.regions.map((part) => kept[part.label] && (
    boxWidthOf(part) >= extentWidth * active.structure.span || boxHeightOf(part) >= extentHeight * active.structure.span
  ));

  const groups = directionGroupsOf(drawing.directions);

  const close = (): { walls: Bitmap; filled: Bitmap; paper: Labelled } => {
    const structure = page.maskOf((label) => structural[label]);
    const { walls, filled } = bridgedAtAngles(structure, groups, maxGap, minRun, active.openings.oneSidedShare);
    return { walls, filled, paper: labelRegions(walls, 0, false) };
  };

  let closedOff = close();

  // A wing drawn apart from the rest is structure too; a table inside a room is not.
  const promoted = parts.regions.filter((part) => {
    if (!kept[part.label] || structural[part.label]) return false;
    if (boxWidthOf(part) * boxHeightOf(part) < extentWidth * extentHeight * active.structure.largeIslandShare) return false;
    const [x, y] = topLeftOf(partOf, width, part);
    const host = x > 0 ? closedOff.paper.labels[(y * width) + x - 1] : -1;
    return host < 0 || closedOff.paper.regions[host].touchesEdge;
  });

  if (promoted.length) {
    promoted.forEach((part) => { structural[part.label] = true; });
    closedOff = close();
  }

  const { walls, filled, paper } = closedOff;

  return {
    structural,
    walls,
    filled,
    paper,
    isOutside: (x, y): boolean => {
      if (x < 0 || y < 0 || x >= width || y >= height) return true;
      const label = paper.labels[(y * width) + x];
      return label >= 0 && paper.regions[label].touchesEdge;
    },
  };
};

/** Step 2: the building — everything not outside — and its outline. */
const buildingOf = (page: Page, spaces: Spaces): Building | undefined => {
  const { width, height, stroke } = page;
  const { walls, paper } = spaces;

  const inside = new Uint8Array(width * height);
  const enclosed = Uint8Array.from(paper.regions, (region) => (region.touchesEdge ? 0 : 1));
  for (let index = 0; index < inside.length; index += 1) {
    const label = paper.labels[index];
    inside[index] = walls.data[index] || (label >= 0 && enclosed[label]) ? 1 : 0;
  }

  // Opened so the dimension lines and arrows drawn off the building are not part of its outline.
  const solid = opened({ width, height, data: inside }, Math.max(2, Math.ceil(stroke * 0.75)));
  const blocks = labelRegions(solid, 1, true);
  const region = blocks.regions.reduce<Region | undefined>((best, block) => (!best || block.area > best.area ? block : best), undefined);

  if (!region || boxWidthOf(region) < width * 0.05 || boxHeightOf(region) < height * 0.05) {
    return undefined;
  }

  const isBuilding = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < width && y < height
    && blocks.labels[(y * width) + x] === region.label;
  const diagonal = Math.hypot(boxWidthOf(region), boxHeightOf(region));

  let border = straightened(
    simplifiedRing(
      traceBoundary(isBuilding, topLeftOf(blocks.labels, width, region), region.area * 4),
      Math.max(1.5, diagonal * 0.004),
    ),
    active.straighten.degrees,
  );

  if (border.length < 4) {
    border = [
      [region.minX, region.minY], [region.maxX + 1, region.minY],
      [region.maxX + 1, region.maxY + 1], [region.minX, region.maxY + 1],
    ];
  }

  return {
    region, area: region.area, inBuilding: ([x, y]) => isBuilding(x, y), border,
  };
};

/** Step 3: the door swings, among the small spaces the closed structure leaves. */
const doorsOf = (page: Page, spaces: Spaces, building: Building): Door[] => {
  const angles = directionGroupsOf(page.drawing.directions).map(({ degrees }) => degrees);
  return spaces.paper.regions
    .filter((region) => region.area < building.area * 0.05 && building.inBuilding(topLeftOf(spaces.paper.labels, page.width, region)))
    // Level first; then along each direction the drawing's walls run in.
    .map((region) => doorOf(spaces.paper.labels, page.width, region, page.minDim, page.stroke)
      ?? angles.reduce<Door | undefined>(
        (found, degrees) => found ?? turnedDoorOf(spaces.paper.labels, page.width, region, degrees, page.minDim, page.stroke),
        undefined,
      ))
    .filter((door): door is Door => door !== undefined);
};

/** The largest share of the building a shape can be whose strokes' gaps `joinedUp` closes across. */
const JOINED_SHARE = 0.1;

/**
 * `ink` with the gaps a shape drawn by hand leaves closed across, up to twice `gap`: a corner a
 * pen did not quite close. Closing the picture (`closed`) fills a gap between strokes that point
 * past each other into a corner, but not the outside of a corner both strokes stop short of,
 * where there is nothing to fill between: so each stroke of `islands` that stops is carried on the
 * way it runs, and kept where it meets other ink, or another stroke carried on. A mark drawn
 * inside a shape — a letter, a chair — is left out of the closing, so it is not joined to the
 * outline round it, splitting the paper inside in two; and put back afterwards.
 */
const joinedUp = (page: Page, islands: readonly Region[], ink: Uint8Array, gap: number, buildingArea: number): Bitmap => {
  const {
    width, height, partOf, minDim,
  } = page;
  const markSize = minDim * active.marks.letterShare;
  const within = (part: Region, other: Region): boolean => part.minX > other.minX && part.maxX < other.maxX
    && part.minY > other.minY && part.maxY < other.maxY;
  const nested = islands.filter((part) => Math.max(boxWidthOf(part), boxHeightOf(part)) <= markSize
    && islands.some((other) => other !== part && within(part, other)));
  const isNested = new Set(nested.map((part) => part.label));

  const base = ink.slice();
  nested.forEach((part) => {
    for (let y = part.minY; y <= part.maxY; y += 1) {
      for (let x = part.minX; x <= part.maxX; x += 1) if (partOf[(y * width) + x] === part.label) base[(y * width) + x] = 0;
    }
  });

  const joined = base.slice();
  const paint = (path: readonly Pixel[]): void => path.forEach(([x, y]) => { joined[(y * width) + x] = 1; });
  const reach = 2 * gap;
  // The strokes carried on that met nothing: they may meet each other, off a corner.
  const loose: Pixel[][] = [];

  // Only strokes of what could close off a desk or a step are carried on: not a letter's, too
  // small to go round one, nor a great outline's, whose gaps are closed as the walls' are.
  const [smallest, largest] = [buildingArea * active.desks.share.min * 0.5, buildingArea * JOINED_SHARE];
  const shapes = islands.filter((part) => !isNested.has(part.label)
    && boxWidthOf(part) * boxHeightOf(part) >= smallest && boxWidthOf(part) * boxHeightOf(part) <= largest);

  shapes.forEach((part) => {
    const [left, top] = [part.minX - 1, part.minY - 1];
    const [cropWidth, cropHeight] = [boxWidthOf(part) + 2, boxHeightOf(part) + 2];
    const own = new Uint8Array(cropWidth * cropHeight);
    for (let y = part.minY; y <= part.maxY; y += 1) {
      for (let x = part.minX; x <= part.maxX; x += 1) {
        if (partOf[(y * width) + x] === part.label) own[((y - top) * cropWidth) + (x - left)] = 1;
      }
    }

    lineEndsOf(thinned({ width: cropWidth, height: cropHeight, data: own }), gap).forEach(({ at, heading }) => {
      // On from the end of the line: out of its own ink, then across paper to whatever it meets.
      const [startX, startY] = [at[0] + left, at[1] + top];
      const path: Pixel[] = [];
      let [clear, met] = [false, false];

      for (let step = 1; step <= reach && !met; step += 1) {
        const [x, y] = [Math.round(startX + (heading[0] * step)), Math.round(startY + (heading[1] * step))];
        if (x < 0 || y < 0 || x >= width || y >= height) break;
        const isInk = base[(y * width) + x] === 1;
        if (!isInk) {
          clear = true;
          path.push([x, y]);
        } else if (clear) {
          met = true;
        }
      }

      if (met) paint(path);
      else if (path.length) loose.push(path);
    });
  });

  // Two strokes both stopping short of a corner, carried on, cross there: each is kept up to it.
  loose.forEach((path, i) => {
    loose.slice(i + 1).forEach((other) => {
      const [[ax, ay], [bx, by]] = [path[0], other[0]];
      if (Math.abs(ax - bx) > 2 * reach || Math.abs(ay - by) > 2 * reach) return;
      const meets = (from: Pixel, to: Pixel): boolean => Math.abs(from[0] - to[0]) <= 1 && Math.abs(from[1] - to[1]) <= 1;
      const here = path.findIndex((pixel) => other.some((there) => meets(pixel, there)));
      if (here < 0) return;
      const there = other.findIndex((pixel) => meets(path[here], pixel));
      paint(path.slice(0, here + 1));
      paint(other.slice(0, there + 1));
    });
  });

  const shut = closed({ width, height, data: joined }, gap);
  nested.forEach((part) => {
    for (let y = part.minY; y <= part.maxY; y += 1) {
      for (let x = part.minX; x <= part.maxX; x += 1) if (partOf[(y * width) + x] === part.label) shut.data[(y * width) + x] = 1;
    }
  });
  return shut;
};

/** Step 4: the small closed shapes on the floor — stairs, windows, desks — and what stands free. */
const objectsOf = (page: Page, spaces: Spaces, building: Building, doors: readonly Door[]): Objects => {
  const {
    drawing, width, height, partOf, minDim, stroke,
  } = page;
  const { kept, parts } = drawing;
  const { walls, paper, structural } = spaces;
  const buildingArea = building.area;
  const doorLabels = new Set(doors.map((door) => door.region.label));

  const islands = parts.regions.filter((part) => kept[part.label] && !structural[part.label]
    && building.inBuilding(topLeftOf(partOf, width, part)));
  const islandLabels = new Set(islands.map((part) => part.label));

  // The fittings first: the loops a symbol is drawn with are not desks, steps or windows.
  const doorWidths = doors.map((door) => door.radius);
  const metresPerPixelFromDoors = doorWidths.length ? active.scale.doorWidthMetres / medianOf(doorWidths) : null;
  const symbols = symbolsAmong(page, islands, metresPerPixelFromDoors);
  const inSymbols = new Set(symbols.flatMap((symbol) => symbol.members));

  // Every closed patch of paper, with the free-standing ink put back so a desk drawn on its own
  // is closed off too.
  const everything = walls.data.slice();
  const isIsland = Uint8Array.from(parts.regions, (part) => (islandLabels.has(part.label) ? 1 : 0));
  for (let index = 0; index < partOf.length; index += 1) {
    const label = partOf[index];
    if (label >= 0 && isIsland[label]) everything[index] = 1;
  }
  const pieces = labelRegions({ width, height, data: everything }, 0, false);

  // The ink a patch of paper lies inside: the part just left of its top-left pixel — looking
  // further left past a gap closed across, when there is one.
  const hostOf = (x: number, y: number, reach: number): number => {
    for (let step = 1; step <= reach && x - step >= 0; step += 1) {
      const label = partOf[(y * width) + x - step];
      if (label >= 0) return label;
    }
    return -1;
  };

  const candidatesOf = (labelled: Labelled, reach: number): Candidate[] => labelled.regions
    .filter((region) => !region.touchesEdge && region.area < buildingArea * active.rooms.minShare * 4)
    .filter((region) => region.area >= buildingArea * active.desks.share.min * 0.5)
    .map((region) => ({ region, at: topLeftOf(labelled.labels, width, region) }))
    .filter(({ at }) => building.inBuilding(at))
    // A door swing is not a desk, whatever its box looks like.
    .filter(({ at: [x, y] }) => !doorLabels.has(paper.labels[(y * width) + x]))
    .map(({ region, at: [x, y] }) => {
      const box = orientedBoxOf(labelled.labels, width, region);
      return {
        region,
        at: [x, y],
        box,
        kind: shapeKindOf(box, region.area, buildingArea),
        host: hostOf(x, y, reach),
      };
    });

  const drawnClosed = candidatesOf(pieces, 1);

  // A shape drawn by hand often does not quite close: a corner left open by a pen stroke or two.
  // Read again with such gaps closed, what closes then — and was not a shape before — is one too.
  const gap = Math.round(stroke * active.objects.gapClosingStrokes);
  let closedUp: Candidate[] = [];
  if (gap > 0 && islands.length) {
    const shapes = islands.filter((part) => !inSymbols.has(part.label));
    const closedPieces = labelRegions(joinedUp(page, shapes, everything, gap, buildingArea), 0, false);
    closedUp = candidatesOf(closedPieces, gap + 2).filter(({ region, kind, at: [x, y] }) => {
      // Only what closing gaps is for — a desk, a step — and only where, before, the same place
      // leaked into something far larger: an open corner lets a desk's paper out into the room.
      // Looked at where the patch starts, which is paper before as after: its middle may be a
      // mark drawn in it.
      if (kind !== 'desk' && kind !== 'tread') return false;
      const before = pieces.labels[(y * width) + x];
      return before >= 0 && pieces.regions[before].area > region.area * 4;
    });
  }

  const candidates: Candidate[] = [...drawnClosed, ...closedUp].filter(({ host }) => !inSymbols.has(host));
  // Candidates are told apart as themselves, not by label: a patch's label counts the patches of
  // paper it is one of, a desk drawn filled in counts the parts of the ink, and the two meet.
  const isClosedUp = new Set(closedUp);

  // Desks drawn filled in, standing free: solid rectangles of a desk's shape and size. Solid in
  // the ink as drawn, not only once thickened — thickening makes a small letter a solid block.
  const { frame } = drawing;
  // Measured against the part's own turned box, so a desk drawn at an angle counts as well.
  const solidAsDrawn = (part: Region, box: OrientedBox): boolean => {
    let ink = 0;
    for (let y = part.minY; y <= part.maxY; y += 1) {
      for (let x = part.minX; x <= part.maxX; x += 1) {
        if (partOf[(y * width) + x] === part.label) ink += frame.data[(y * width) + x];
      }
    }
    return ink >= Math.max(1, (box.length - 2) * (box.breadth - 2)) * 0.85;
  };

  const solidDesks: Candidate[] = islands
    .filter((part) => !inSymbols.has(part.label))
    .map((part) => ({ part, box: orientedBoxOf(partOf, width, part) }))
    .filter(({ box }) => box.fill >= 0.85)
    .filter(({ part, box }) => solidAsDrawn(part, box))
    .filter(({ part, box }) => shapeKindOf(box, part.area, buildingArea) === 'desk')
    .map(({ part, box }) => ({
      region: part, at: topLeftOf(partOf, width, part), box, kind: 'desk' as const, host: part.label, solid: true,
    }));

  // Staircases: runs of treads of one size, one direction, side by side.
  // Desks are included so a flight whose steps happen to be desk-shaped is still one flight; a
  // flight needs at least one step too narrow to be a desk, so a row of desks is never stairs.
  const treads = candidates.filter(({ kind, region }) => kind !== 'other' && region.area <= buildingArea * active.desks.share.max);
  const group = treads.map((_, index) => index);
  const root = (index: number): number => (group[index] === index ? index : root(group[index]));

  treads.forEach((a, i) => {
    treads.forEach((b, j) => {
      if (j <= i || turnBetween(a.box.angleDegrees, b.box.angleDegrees) > 8) return;
      if (b.box.length < a.box.length * 0.75 || b.box.length > a.box.length * 1.33) return;

      const radians = (a.box.angleDegrees * Math.PI) / 180;
      const dx = b.box.centre[0] - a.box.centre[0];
      const dy = b.box.centre[1] - a.box.centre[1];
      const along = Math.abs((dx * Math.cos(radians)) + (dy * Math.sin(radians)));
      const across = Math.abs((dy * Math.cos(radians)) - (dx * Math.sin(radians)));

      if (along <= a.box.length * 0.25 && across <= a.box.breadth + b.box.breadth + (3 * stroke) + 2) {
        group[root(j)] = root(i);
      }
    });
  });

  const flights = new Map<number, Candidate[]>();
  treads.forEach((tread, index) => {
    const key = root(index);
    flights.set(key, [...(flights.get(key) ?? []), tread]);
  });

  const stairs = Array.from(flights.values()).filter((flight) => flight.length >= active.stairs.minTreads
    && flight.some(({ kind }) => kind !== 'desk'));
  const onStairs = new Set(stairs.flat());
  // The outline a flight is drawn in is the staircase, not furniture.
  const stairOutlines = new Set(stairs.flat().map(({ host }) => host).filter((host) => host >= 0));

  // Windows: strips with the outside just beyond one of their long sides.
  const windows = candidates.filter((candidate) => {
    const { kind, box } = candidate;
    if (kind !== 'strip' || onStairs.has(candidate) || box.breadth > Math.max(3 * stroke, minDim * 0.02)) return false;
    const reach = box.breadth + (3 * stroke) + 2;
    const radians = (box.angleDegrees * Math.PI) / 180;
    const [nx, ny] = [-Math.sin(radians), Math.cos(radians)];
    return [-0.3, 0, 0.3].some((fraction) => [-1, 1].some((side) => spaces.isOutside(
      Math.round(box.centre[0] + (Math.cos(radians) * fraction * box.length) + (side * nx * reach)),
      Math.round(box.centre[1] + (Math.sin(radians) * fraction * box.length) + (side * ny * reach)),
    )));
  });

  // Desks. Their size is checked against a real one when the doors say how big things are,
  // and against each other when nothing does: a plan's desks are mostly one size.
  let desks = [
    ...candidates.filter((candidate) => candidate.kind === 'desk' && !onStairs.has(candidate)),
    ...solidDesks,
  ];

  if (metresPerPixelFromDoors) {
    desks = desks.filter((desk) => {
      const size = drawnSizeOf(desk, stroke);
      const length = size.length * metresPerPixelFromDoors;
      const depth = size.depth * metresPerPixelFromDoors;
      return length >= active.desks.metres.length.min && length <= active.desks.metres.length.max
        && depth >= active.desks.metres.depth.min && depth <= active.desks.metres.depth.max;
    });
  } else if (desks.length >= 3) {
    const typical = medianOf(desks.map((desk) => drawnSizeOf(desk, stroke).length));
    desks = desks.filter((desk) => {
      const { length } = drawnSizeOf(desk, stroke);
      return length >= typical * 0.6 && length <= typical * 1.6;
    });
  }

  return {
    islands,
    islandLabels,
    candidates,
    stairs,
    stairOutlines,
    windows,
    desks,
    islandsWithDesks: new Set([
      ...desks.map(({ host }) => host).filter((host) => islandLabels.has(host)),
      // A desk closed up from pieces is drawn with all of them.
      ...desks.filter((desk) => isClosedUp.has(desk)).flatMap(({ region }) => islands
        .filter((part) => part.maxX >= region.minX - gap - stroke && part.minX <= region.maxX + gap + stroke
          && part.maxY >= region.minY - gap - stroke && part.minY <= region.maxY + gap + stroke)
        .map((part) => part.label)),
    ]),
    metresPerPixelFromDoors,
    symbols,
    inSymbols,
  };
};

/** Step 5: the rooms — every space large enough, in its real shape, with its door swings part of it. */
const roomsOf = (page: Page, spaces: Spaces, building: Building, doors: readonly Door[]): SketchRoom[] => {
  const {
    width, height, stroke, outlineOf,
  } = page;
  const { paper } = spaces;
  const buildingArea = building.area;
  const doorLabels = new Set(doors.map((door) => door.region.label));

  // The room a door swings into: just past the swing, along its middle.
  const swingsInto = new Map<number, Door[]>();
  doors.forEach((door) => {
    const reach = door.radius + (2 * stroke) + 2;
    const x = Math.round(door.hinge[0] + (door.towards[0] * reach));
    const y = Math.round(door.hinge[1] + (door.towards[1] * reach));
    const room = x >= 0 && y >= 0 && x < width && y < height ? paper.labels[(y * width) + x] : -1;
    if (room >= 0) swingsInto.set(room, [...(swingsInto.get(room) ?? []), door]);
  });

  const rooms: SketchRoom[] = [];

  paper.regions
    .filter((region) => !region.touchesEdge && !doorLabels.has(region.label))
    .filter((region) => region.area >= buildingArea * active.rooms.minShare && region.area < buildingArea * active.rooms.openFloorShare)
    .filter((region) => building.inBuilding(topLeftOf(paper.labels, width, region)))
    .forEach((region) => {
      // A desk or a step against a wall closes off a patch as a room does; it is still a desk.
      if (region.area <= buildingArea * active.rooms.minShare * 4) {
        const kind = shapeKindOf(orientedBoxOf(paper.labels, width, region), region.area, buildingArea);
        if (kind !== 'other') return;
      }

      const swings = swingsInto.get(region.label) ?? [];
      const boxArea = boxWidthOf(region) * boxHeightOf(region);
      const area = region.area + swings.reduce((total, door) => total + door.region.area, 0);

      // The patch stops a pixel short of the line on every side, because the line was thickened
      // to close its gaps; the room's edge is the middle of the line, one pixel further out.
      if (area / boxArea >= active.rooms.rectangularFill) {
        const [left, top, right, bottom] = [region.minX - 1, region.minY - 1, region.maxX + 2, region.maxY + 2];
        rooms.push({ name: null, outline: outlineOf([[left, top], [right, top], [right, bottom], [left, bottom]]), confidence: 0.9 });
        return;
      }

      // Otherwise its real shape, with each door swing into it counted as part of it rather
      // than as a bite out of it.
      const members = new Set([region.label, ...swings.map((door) => door.region.label)]);
      const grow = swings.length ? Math.ceil(stroke) + 1 : 0;
      const pad = grow + 2;
      const left = Math.max(0, Math.min(region.minX, ...swings.map((door) => door.region.minX)) - pad);
      const top = Math.max(0, Math.min(region.minY, ...swings.map((door) => door.region.minY)) - pad);
      const right = Math.min(width - 1, Math.max(region.maxX, ...swings.map((door) => door.region.maxX)) + pad);
      const bottom = Math.min(height - 1, Math.max(region.maxY, ...swings.map((door) => door.region.maxY)) + pad);
      const subWidth = (right - left) + 1;
      const subHeight = (bottom - top) + 1;
      const sub = new Uint8Array(subWidth * subHeight);

      for (let y = top; y <= bottom; y += 1) {
        for (let x = left; x <= right; x += 1) {
          if (members.has(paper.labels[(y * width) + x])) sub[((y - top) * subWidth) + (x - left)] = 1;
        }
      }

      const shape = closed({ width: subWidth, height: subHeight, data: sub }, grow);
      const start = shape.data.findIndex((value) => value === 1);
      if (start < 0) return;

      const traced = traceBoundary(
        (x, y) => x >= 0 && y >= 0 && x < subWidth && y < subHeight && shape.data[(y * subWidth) + x] === 1,
        [start % subWidth, Math.floor(start / subWidth)],
        subWidth * subHeight * 4,
      );
      const corners = straightened(
        simplifiedRing(traced, Math.max(1.5, Math.hypot(boxWidthOf(region), boxHeightOf(region)) * 0.01)),
        active.straighten.degrees,
      );

      if (corners.length >= 3) {
        rooms.push({ name: null, outline: outlineOf(corners.map(([x, y]): Pixel => [x + left, y + top])), confidence: 0.75 });
      }
    });

  return rooms;
};

/** A wall drawn thicker than this (thickened) is a wall with a thickness, not a line. */
const thickWallOf = (stroke: number): number => Math.max(stroke * active.walls.thickFactor, stroke + active.walls.thickExtraPixels);

/** Where a straight run of wall found on a turned copy of the picture lies on the picture itself. */
interface Frame {
  readonly bitmap: Bitmap;
  readonly toSource: (x: number, y: number) => [number, number];
  /**
   * Whether the frame is the picture turned. Turning a picture pixel by pixel can gain or lose a
   * row of a wall, so a wall found turned is measured across on the picture itself.
   */
  readonly turned: boolean;
}

/**
 * How thick the ink of `mask` is across the line from `from` to `to`: at five places along it,
 * the unbroken ink through the line, a quarter pixel at a time, taking the middle of the five.
 */
const thicknessAcross = (mask: Bitmap, from: [number, number], to: [number, number], reach: number): number => {
  const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
  const [ux, uy] = [(to[0] - from[0]) / length, (to[1] - from[1]) / length];
  const inkAt = (x: number, y: number): boolean => {
    const [px, py] = [Math.round(x), Math.round(y)];
    return px >= 0 && py >= 0 && px < mask.width && py < mask.height && mask.data[(py * mask.width) + px] === 1;
  };
  const step = 0.25;

  const widths = [0.15, 0.3, 0.5, 0.7, 0.85].map((fraction) => {
    const [cx, cy] = [from[0] + (ux * fraction * length), from[1] + (uy * fraction * length)];
    if (!inkAt(cx, cy)) return 0;
    let [before, after] = [0, 0];
    while (before < reach && inkAt(cx - (uy * (before + step)), cy + (ux * (before + step)))) before += step;
    while (after < reach && inkAt(cx + (uy * (after + step)), cy - (ux * (after + step)))) after += step;
    return before + after + 1;
  });

  return medianOf(widths);
};

/** A wall as found, in the picture's pixels, before it is put in sketch coordinates. */
interface FoundWall {
  readonly from: [number, number];
  readonly to: [number, number];
  readonly thickness: number;
  /** How much thicker than drawn the line was made, closing its gaps: more for a wall aslant. */
  readonly grown: number;
  readonly drawnThick: boolean;
  readonly kind: SketchWallKind;
  readonly turned: boolean;
}

/**
 * The walls among the straight bars of `frame`: those drawn thick, and those standing free with
 * the same room on both sides, told outside, inside or partition by what lies either side.
 */
const wallsInFrame = (page: Page, spaces: Spaces, frame: Frame, bars: readonly Bar[], structure: Bitmap): FoundWall[] => {
  const { width, height, stroke } = page;
  const { paper, isOutside } = spaces;
  const thick = thickWallOf(stroke);
  const labelAt = (x: number, y: number): number => (x >= 0 && y >= 0 && x < width && y < height ? paper.labels[(y * width) + x] : -2);
  const source = (x: number, y: number): [number, number] => frame.toSource(x, y);
  const pixelAt = (x: number, y: number): Pixel => {
    const [sx, sy] = source(x, y);
    return [Math.round(sx), Math.round(sy)];
  };
  const found: FoundWall[] = [];

  bars.forEach((bar) => {
    const thickness = (bar.last - bar.first) + 1;
    const length = (bar.end - bar.start) + 1;

    if (thickness * 2 > length) return;

    const middle = (bar.first + bar.last + 1) / 2;
    const from = bar.horizontal ? source(bar.start, middle) : source(middle, bar.start);
    const to = bar.horizontal ? source(bar.end + 1, middle) : source(middle, bar.end + 1);
    const measured = frame.turned ? thicknessAcross(structure, from, to, thickness * 2) : thickness;
    if (frame.turned && Math.hypot(to[0] - from[0], to[1] - from[1]) < measured * active.angledWalls.aspect) return;

    // Every line was thickened a pixel each side, by a square: across a line aslant, a square
    // reaches further than across one level.
    const radians = Math.atan2(to[1] - from[1], to[0] - from[0]);
    const grown = frame.turned ? 2 * (Math.abs(Math.cos(radians)) + Math.abs(Math.sin(radians))) : 2;

    // What lies either side of a wall found turned: looked for on the picture itself, clear of the
    // wall by the thickness it measures there.
    const [ux, uy] = [Math.cos(radians), Math.sin(radians)];
    const clear = (measured / 2) + 2;
    const besideTurned = (fraction: number, side: number): Pixel => [
      Math.round(from[0] + ((to[0] - from[0]) * fraction) - (side * uy * clear)),
      Math.round(from[1] + ((to[1] - from[1]) * fraction) + (side * ux * clear)),
    ];

    // What lies on each side of the wall, at a few places along it.
    const sides = [0.15, 0.3, 0.5, 0.7, 0.85].map((fraction) => {
      const along = Math.round(bar.start + (fraction * (length - 1)));
      let before: Pixel;
      let after: Pixel;
      if (frame.turned) {
        [before, after] = [besideTurned(fraction, -1), besideTurned(fraction, 1)];
      } else {
        before = bar.horizontal ? pixelAt(along, bar.first - 2) : pixelAt(bar.first - 2, along);
        after = bar.horizontal ? pixelAt(along, bar.last + 2) : pixelAt(bar.last + 2, along);
      }
      const beforeLabel = labelAt(before[0], before[1]);
      return {
        outside: isOutside(before[0], before[1]) || isOutside(after[0], after[1]),
        same: beforeLabel >= 0 && beforeLabel === labelAt(after[0], after[1]),
      };
    });

    const exterior = sides.filter((side) => side.outside).length >= 3;
    const freeStanding = sides.filter((side) => side.same).length >= 3;
    const drawnThick = measured >= thick;

    if (!drawnThick && !freeStanding) return;

    let kind: SketchWallKind = 'interior';
    if (exterior) kind = 'exterior';
    else if (freeStanding) kind = 'partition';

    found.push({
      from, to, thickness: measured, grown, drawnThick, kind, turned: frame.turned,
    });
  });

  return found;
};

const directionOf = (wall: FoundWall): number => {
  const degrees = (Math.atan2(wall.to[1] - wall.from[1], wall.to[0] - wall.from[0]) * 180) / Math.PI;
  return ((degrees % 180) + 180) % 180;
};

const lengthOfWall = (wall: FoundWall): number => Math.hypot(wall.to[0] - wall.from[0], wall.to[1] - wall.from[1]);

/** Whether `point` lies on `other`: along it, and across it within its thickness and `slack`. */
const liesOn = (point: readonly [number, number], other: FoundWall, slack: number): boolean => {
  const length = lengthOfWall(other);
  if (length <= 0) return false;
  const [ux, uy] = [(other.to[0] - other.from[0]) / length, (other.to[1] - other.from[1]) / length];
  const [mx, my] = [point[0] - other.from[0], point[1] - other.from[1]];
  const along = (mx * ux) + (my * uy);
  const across = Math.abs((my * ux) - (mx * uy));
  return along >= 0 && along <= length && across <= (other.thickness / 2) + slack;
};

/** Whether `wall`'s middle lies on `other`, within its thickness and a stroke. */
const lies = (wall: FoundWall, other: FoundWall, stroke: number): boolean => liesOn(
  [(wall.from[0] + wall.to[0]) / 2, (wall.from[1] + wall.to[1]) / 2],
  other,
  stroke,
);

/** Whether all of `wall`, end to end, lies inside `other`. */
const liesWithin = (wall: FoundWall, other: FoundWall): boolean => liesOn(wall.from, other, 0) && liesOn(wall.to, other, 0);

/**
 * Step 6: the straight walls — those drawn thick, and those standing free whatever their
 * thickness — level and plumb, and along every other direction the drawing's lines run in: for
 * each, the structure is turned so that direction is level, and its walls are read off in rows
 * the same way. A direction found with the one square to it is read in one turn, both ways.
 */
const wallsOf = (page: Page, spaces: Spaces): SketchWall[] => {
  const {
    width, height, minDim, stroke, normalise, lengthOf, drawing,
  } = page;
  const structureOnly = page.maskOf((label) => spaces.structural[label]);
  const minBar = Math.max(active.walls.minBarStrokes * stroke, minDim * active.walls.minBarShare);

  const square: Frame = { bitmap: structureOnly, toSource: (x, y) => [x, y], turned: false };
  const bars = [...barsAlong(structureOnly, true, minBar), ...barsAlong(structureOnly, false, minBar)];
  let found = wallsInFrame(page, spaces, square, bars, structureOnly);

  directionGroupsOf(drawing.directions).forEach(({ degrees, squareToo }) => {
    // Turned so the direction lies level; walls square to it then lie plumb.
    const turn = turnOf(width, height, -degrees);
    const turned: Frame = { bitmap: turnedMask(structureOnly, -degrees), toSource: turn.toSource, turned: true };
    const turnedBars = [
      ...barsAlong(turned.bitmap, true, minBar),
      ...(squareToo ? barsAlong(turned.bitmap, false, minBar) : []),
    ];
    found = [...found, ...wallsInFrame(page, spaces, turned, turnedBars, structureOnly)];
  });

  // A wall read along a direction a few degrees off its own is still that wall: the longer keeps
  // it. Measured against the walls kept, so a wall left out as another's copy leaves out no more.
  const unique: FoundWall[] = [];
  found
    .slice()
    .sort((a, b) => lengthOfWall(b) - lengthOfWall(a))
    .forEach((wall) => {
      const copy = unique.some((other) => {
        const turnApart = Math.abs(directionOf(wall) - directionOf(other));
        return Math.min(turnApart, 180 - turnApart) < active.angledWalls.sameWallDegrees && lies(wall, other, stroke);
      });
      if (!copy) unique.push(wall);
    });
  // What a turned read finds wholly inside a level or plumb wall is where walls join, not a wall.
  const kept = unique.filter((wall, _, walls) => !wall.turned || !walls.some((other) => !other.turned && liesWithin(wall, other)));

  // The line was thickened by a pixel each side to close its gaps.
  return kept.map((wall): SketchWall => ({
    from: normalise(wall.from[0], wall.from[1]),
    to: normalise(wall.to[0], wall.to[1]),
    thickness: wall.drawnThick ? lengthOf(Math.max(1, wall.thickness - wall.grown)) : null,
    kind: wall.kind,
    confidence: 0.85,
  }));
};

/**
 * Step 7: writing. Letters sit side by side on a line, much of a height, a letter's height or less
 * apart; two or more together are a room name, a label, a number. It cannot be read here, so it is
 * offered in review as a label to type in. A mark right by a desk is its chair, not a letter.
 */
const writingOf = (page: Page, objects: Objects): { labels: SketchLabel[]; inWriting: ReadonlySet<number> } => {
  const { minDim, stroke, normalise } = page;
  const letterSize = minDim * active.marks.letterShare;
  const deskBoxes = objects.desks.map(({ region }) => region);

  const byADesk = (part: Region): boolean => {
    const reach = boxHeightOf(part) + stroke;
    return deskBoxes.some((desk) => part.maxX + reach >= desk.minX && part.minX - reach <= desk.maxX
      && part.maxY + reach >= desk.minY && part.minY - reach <= desk.maxY);
  };

  const letters = objects.islands
    .filter((part) => !objects.islandsWithDesks.has(part.label) && !objects.stairOutlines.has(part.label))
    .filter((part) => !objects.inSymbols.has(part.label))
    .filter((part) => boxHeightOf(part) <= letterSize && boxWidthOf(part) <= boxHeightOf(part) * 1.6)
    .filter((part) => !byADesk(part))
    .sort((a, b) => a.minX - b.minX);
  const line = letters.map((_, index) => index);
  const lineOf = (index: number): number => (line[index] === index ? index : lineOf(line[index]));

  letters.forEach((a, i) => {
    const reach = (other: Region): number => a.maxX + (1.2 * Math.max(boxHeightOf(a), boxHeightOf(other)));

    for (let j = i + 1; j < letters.length && letters[j].minX <= reach(letters[j]); j += 1) {
      const b = letters[j];
      const overlap = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY) + 1;
      const shorter = Math.min(boxHeightOf(a), boxHeightOf(b));
      const taller = Math.max(boxHeightOf(a), boxHeightOf(b));

      if (overlap >= shorter * 0.5 && taller <= shorter * 2.2) {
        line[lineOf(j)] = lineOf(i);
      }
    }
  });

  const words = new Map<number, Region[]>();
  letters.forEach((letter, index) => {
    const key = lineOf(index);
    words.set(key, [...(words.get(key) ?? []), letter]);
  });

  const writing = Array.from(words.values()).filter((word) => word.length >= 2);

  const labels = writing.map((word): SketchLabel => {
    const left = Math.min(...word.map((letter) => letter.minX));
    const top = Math.min(...word.map((letter) => letter.minY));
    const right = Math.max(...word.map((letter) => letter.maxX)) + 1;
    const bottom = Math.max(...word.map((letter) => letter.maxY)) + 1;
    const centre = normalise((left + right) / 2, (top + bottom) / 2);
    const [from, to] = [normalise(left, top), normalise(right, bottom)];
    return {
      text: '',
      x: centre.x,
      y: centre.y,
      box: {
        left: from.x, top: from.y, right: to.x, bottom: to.y,
      },
      // Two marks side by side may be a pair of anything; a longer word is surely writing.
      confidence: word.length >= 3 ? 0.75 : 0.5,
    };
  });

  return { labels, inWriting: new Set(writing.flat().map((letter) => letter.label)) };
};

/**
 * Step 8: everything else on the floor, as areas — doors, windows, stairs, cupboards against a
 * wall, and what stands free: columns, tables, sofas — plus the partitions standing free.
 */
const furnitureOf = (
  page: Page,
  building: Building,
  doors: readonly Door[],
  objects: Objects,
  inWriting: ReadonlySet<number>,
): { areas: SketchArea[]; icons: SketchIcon[]; partitions: SketchWall[] } => {
  const {
    width, height, partOf, minDim, stroke, normalise, lengthOf, outlineOf,
  } = page;
  const areas: SketchArea[] = [];
  const icons: SketchIcon[] = [];
  const partitions: SketchWall[] = [];
  const addArea = (kind: SketchAreaKind, points: readonly Pixel[], confidence: number): void => {
    if (points.length >= 3) areas.push({ kind, outline: outlineOf(points), confidence });
  };

  // Each door as the quarter circle it sweeps: drawn from its hinge and width rather than traced,
  // because a traced swing a few dozen pixels across comes out as a handful of flat edges.
  doors.forEach((door) => {
    const [hx, hy] = door.hinge;
    const [sx, sy] = door.opens;
    const reach = door.radius + 0.5;
    const sweep = Array.from({ length: DOOR_ARC_STEPS + 1 }, (_, step): Pixel => {
      const radians = ((step / DOOR_ARC_STEPS) * Math.PI) / 2;
      if (door.arms) {
        const [[ax, ay], [bx, by]] = door.arms;
        const [a, b] = [reach * Math.cos(radians), reach * Math.sin(radians)];
        return [hx + (ax * a) + (bx * b), hy + (ay * a) + (by * b)];
      }
      return [hx + (sx * reach * Math.cos(radians)), hy + (sy * reach * Math.sin(radians))];
    });
    addArea('door', [door.hinge, ...sweep], 0.9);
  });

  // A window drawn as three lines is two strips of glass side by side: one window.
  const panes = objects.windows.map(({ box }) => box);
  panesTogether(panes, stroke).forEach((group) => {
    addArea('window', windowOutlineOf(group.map((index) => panes[index]), stroke / 2), group.length > 1 ? 0.85 : 0.8);
  });

  objects.stairs.forEach((flight) => {
    const left = Math.min(...flight.map(({ region }) => region.minX)) - stroke;
    const top = Math.min(...flight.map(({ region }) => region.minY)) - stroke;
    const right = Math.max(...flight.map(({ region }) => region.maxX)) + 1 + stroke;
    const bottom = Math.max(...flight.map(({ region }) => region.maxY)) + 1 + stroke;
    addArea('stairs', [[left, top], [right, top], [right, bottom], [left, bottom]], 0.8);
    const centre = normalise((left + right) / 2, (top + bottom) / 2);
    icons.push({ iconName: 'stairs', x: centre.x, y: centre.y });
  });

  // Rectangles closed off against a wall that are not desks, rooms or steps: counters, cupboards.
  const taken = new Set([...objects.desks, ...objects.stairs.flat(), ...objects.windows]);

  objects.candidates
    .filter((candidate) => !taken.has(candidate) && !objects.islandLabels.has(candidate.host)
      && candidate.region.area < building.area * active.rooms.minShare)
    .filter(({ box, kind }) => kind !== 'strip' && box.fill >= 0.8 && box.breadth >= 2 * stroke)
    .forEach(({ box }) => addArea('furniture', cornersOf(box, stroke / 2), 0.5));

  const markSize = minDim * active.marks.markShare;
  const thick = thickWallOf(stroke);

  // A part's outline as drawn: a round table needs a few dozen corners to look round, not hundreds.
  const drawnOutlineOf = (part: Region): Pixel[] => {
    const traced = traceBoundary(
      (x, y) => x >= 0 && y >= 0 && x < width && y < height && partOf[(y * width) + x] === part.label,
      topLeftOf(partOf, width, part),
      part.area * 4,
    );
    let tolerance = Math.max(1, stroke * 0.6);
    let outline = simplifiedRing(traced, tolerance);

    while (outline.length > 48) {
      tolerance *= 1.5;
      outline = simplifiedRing(traced, tolerance);
    }

    return straightened(outline, active.straighten.furnitureDegrees);
  };

  // The fittings, in their drawn shape, each with its icon.
  objects.symbols.forEach((symbol) => {
    addArea(symbol.kind, drawnOutlineOf(symbol.part), symbol.confidence);
    const box = orientedBoxOf(partOf, width, symbol.part);
    const centre = normalise(box.centre[0], box.centre[1]);
    icons.push({ iconName: SYMBOL_ICONS[symbol.kind], x: centre.x, y: centre.y });
  });

  objects.islands.forEach((part) => {
    if (objects.inSymbols.has(part.label)) return;
    // Writing is a label; a lone letter, a number or a chair too small to be anything of its own.
    if (inWriting.has(part.label)) return;
    if (boxWidthOf(part) <= markSize && boxHeightOf(part) <= markSize) return;
    if (objects.islandsWithDesks.has(part.label) || objects.stairOutlines.has(part.label)) return;

    const box = orientedBoxOf(partOf, width, part);
    const aspect = box.length / Math.max(1, box.breadth);

    if (box.fill >= 0.85 && aspect <= 1.6 && box.length <= minDim * active.columns.share) {
      addArea('column', cornersOf(box), 0.7);
      return;
    }

    if (aspect >= 6 && (box.fill >= 0.85 || box.breadth <= 2.5 * stroke)) {
      const radians = (box.angleDegrees * Math.PI) / 180;
      const [ux, uy] = [Math.cos(radians) * (box.length / 2), Math.sin(radians) * (box.length / 2)];
      partitions.push({
        from: normalise(box.centre[0] - ux, box.centre[1] - uy),
        to: normalise(box.centre[0] + ux, box.centre[1] + uy),
        thickness: box.breadth >= thick ? lengthOf(Math.max(1, box.breadth - 2)) : null,
        kind: 'partition',
        confidence: 0.7,
      });
      return;
    }

    addArea('furniture', drawnOutlineOf(part), 0.7);
  });

  return { areas, icons, partitions };
};

/**
 * Step 8b: doorways drawn as a plain gap in the wall, with no swing — which is how many plans and
 * most sketches draw them. They are the openings `bridged` closed across to keep rooms apart, so
 * they are found as exactly that: the patches it filled that are a door's width, in the building,
 * and not the doorway of a swing already found. Offered as doors, marked uncertain.
 */
const openingsOf = (
  page: Page,
  spaces: Spaces,
  building: Building,
  doors: readonly Door[],
  metresPerPixel: number | null,
): SketchArea[] => {
  const {
    width, height, minDim, outlineOf,
  } = page;
  const structure = page.maskOf((label) => spaces.structural[label]);
  const filled = spaces.filled.data;

  const doorWidth = metresPerPixel ? active.scale.doorWidthMetres / metresPerPixel : null;
  const [shortest, widest] = doorWidth
    ? [doorWidth * active.openings.doors.min, doorWidth * active.openings.doors.max]
    : [minDim * 0.02, minDim * 0.1];
  const patches = labelRegions({ width, height, data: filled }, 1, true);

  const openings = patches.regions
    .filter((patch) => building.inBuilding(topLeftOf(patches.labels, width, patch)))
    .map((patch) => orientedBoxOf(patches.labels, width, patch))
    .filter((box) => box.length >= shortest && box.length <= widest && box.breadth <= box.length * 0.6)
    // A doorway closed across is a rectangle; a wedge is the corner where two walls meet aslant.
    .filter((box) => box.fill >= active.openings.fill)
    // A swing's own doorway is already its door.
    .filter((box) => !doors.some((door) => Math.hypot(box.centre[0] - door.hinge[0], box.centre[1] - door.hinge[1]) <= door.radius * 1.5));

  // A doorway is open on both sides: a room, a corridor, the outside. What was closed across
  // between lines drawn on both of its long sides is glass between a window's frame lines.
  const framedOnBothSides = (box: OrientedBox): boolean => {
    const radians = (box.angleDegrees * Math.PI) / 180;
    const [ux, uy] = [Math.cos(radians), Math.sin(radians)];
    const [nx, ny] = [-uy, ux];
    const reach = (box.breadth / 2) + 2;
    const inkBeyond = (side: number): boolean => [-0.3, 0, 0.3].some((fraction) => {
      const x = Math.round(box.centre[0] + (ux * fraction * box.length) + (side * nx * reach));
      const y = Math.round(box.centre[1] + (uy * fraction * box.length) + (side * ny * reach));
      return x >= 0 && y >= 0 && x < width && y < height && structure.data[(y * width) + x] === 1;
    });
    return inkBeyond(-1) && inkBeyond(1);
  };

  const glass = openings.filter(framedOnBothSides);
  // A doorway is as thick as the wall it is in: a sliver is a wobble in a hand-drawn line, filled in.
  const doorways = openings.filter((box) => !framedOnBothSides(box) && box.breadth >= page.stroke * 0.75);

  return [
    ...doorways.map((box): SketchArea => ({ kind: 'door', outline: outlineOf(cornersOf(box)), confidence: 0.55 })),
    ...panesTogether(glass, page.stroke).map((group): SketchArea => ({
      kind: 'window',
      outline: outlineOf(windowOutlineOf(group.map((index) => glass[index]), page.stroke / 2)),
      confidence: 0.7,
    })),
  ];
};

/**
 * The drawing's own ink, for keeping as drawn what nothing on the plan replaces (`as-drawn`):
 * without a sheet frame round it, and without what lies well outside the building — a title
 * block, a legend — but with what is drawn just outside a wall, which belongs to the plan.
 */
const drawnInkOf = (page: Page, building: Building): SketchInk => {
  const { width, height, stroke, drawing } = page;
  const inside: Bitmap = { width, height, data: new Uint8Array(width * height) };
  fillPolygon(inside, building.border);
  strokeOutline(inside, building.border, Math.max(stroke, page.minDim * active.asDrawn.marginShare));

  const data = new Uint8Array(width * height);
  const { data: ink } = drawing.frame;
  const partOf = drawing.parts.labels;
  for (let index = 0; index < ink.length; index += 1) {
    const part = partOf[index];
    if (ink[index] && inside.data[index] && part >= 0 && drawing.kept[part]) data[index] = 1;
  }

  return {
    width, height, data, stroke,
  };
};

/** The walls, doors, rooms and everything on the floor, closing wall openings up to `maxGap`. */
const read = (drawing: Drawing, maxGap: number): Reading | undefined => {
  const page = pageOf(drawing);
  starting('spaces');
  const spaces = spacesOf(page, maxGap);
  starting('building');
  const building = buildingOf(page, spaces);

  if (!building) {
    return undefined;
  }

  starting('doors');
  const doors = doorsOf(page, spaces, building);
  starting('objects');
  const objects = objectsOf(page, spaces, building, doors);
  starting('rooms');
  const rooms = roomsOf(page, spaces, building, doors);
  starting('walls');
  const walls = wallsOf(page, spaces);
  starting('writing');
  const { labels, inWriting } = writingOf(page, objects);
  starting('furniture');
  const { areas, icons, partitions } = furnitureOf(page, building, doors, objects, inWriting);
  starting('openings');
  const openings = openingsOf(page, spaces, building, doors, objects.metresPerPixelFromDoors);

  const { stroke, normalise, lengthOf } = page;
  const deskLengths = objects.desks.map((desk) => drawnSizeOf(desk, stroke).length);
  const typicalDesk = deskLengths.length ? medianOf(deskLengths) : 0;
  const desks = objects.desks.map((desk): SketchDesk => {
    const centre = normalise(desk.box.centre[0], desk.box.centre[1]);
    const size = drawnSizeOf(desk, stroke);
    return {
      x: centre.x,
      y: centre.y,
      rotationDegrees: planDegreesOf(desk.box.angleDegrees),
      width: lengthOf(size.length),
      depth: lengthOf(size.depth),
      confidence: deskConfidenceOf(desk, typicalDesk, stroke),
    };
  });

  // Step 9: the scale — from the doors when there are any, else from the desks.
  const doorWidths = doors.map((door) => door.radius);
  let metresPerPixel = objects.metresPerPixelFromDoors;
  let scaleSource: SketchScaleSource | null = metresPerPixel ? 'doors' : null;

  if (!metresPerPixel && deskLengths.length >= 2) {
    metresPerPixel = active.scale.deskLengthMetres / medianOf(deskLengths);
    scaleSource = 'desks';
  }

  const sketch: FloorPlanSketch = {
    isFloorPlan: true,
    border: page.outlineOf(building.border),
    rooms,
    walls: [...walls, ...partitions],
    desks,
    labels,
    areas: [...areas, ...openings],
    icons,
    metresAcrossImage: metresPerPixel ? Math.round(metresPerPixel * page.width * 1000) / 1000 : null,
    scaleSource,
    aspect: drawing.aspect,
    frame: drawing.turn,
  };

  return {
    sketch,
    doorWidths,
    deskLengths,
    ink: () => drawnInkOf(page, building),
  };
};

export interface TraceOptions {
  /**
   * Whether a picture taken at a slight angle is turned square first. On unless the administrator
   * has turned the picture themselves, when the angle is theirs to choose.
   */
  readonly straighten?: boolean;
  /** Thresholds to read with instead of the defaults: for trying a change against real scans. */
  readonly config?: TraceConfigOverride;
  /** Told as each step of the trace starts. */
  readonly onStage?: StageListener;
}

/** What was found on a drawing, and the drawing's own ink to keep the rest of it as drawn. */
export interface TracedDrawing {
  readonly sketch: FloorPlanSketch;
  /** Null when no building was found: there is nothing to keep. */
  readonly ink: SketchInk | null;
}

const traceWith = (ink: Uint8Array, width: number, height: number, options: TraceOptions): TracedDrawing => {
  const empty: TracedDrawing = {
    sketch: {
      isFloorPlan: false, border: [], rooms: [], walls: [], desks: [], labels: [], metresAcrossImage: null,
    },
    ink: null,
  };

  const source: Bitmap = { width, height, data: ink };
  starting('straighten');
  const correction = options.straighten === false ? 0 : skewCorrectionOf(source, active.skew.rangeDegrees);
  const turned = Math.abs(correction) >= active.skew.minimumDegrees;
  const frame = turned ? rotateBitmap(source, correction) : source;

  // Every line a pixel thicker each side: closes the small gaps a pen leaves in a line.
  starting('parts');
  const lines = dilate(frame, 1);
  const parts = labelRegions(lines, 1, true);

  // A sheet frame is a border round the drawing, not the building's walls.
  const kept = parts.regions.map((part) => {
    const partWidth = boxWidthOf(part);
    const partHeight = boxHeightOf(part);

    if (partWidth < frame.width * active.sheetFrame.cover || partHeight < frame.height * active.sheetFrame.cover) return true;

    const band = Math.max(2, Math.min(partWidth, partHeight) * active.sheetFrame.band);
    let alongEdge = 0;

    for (let y = part.minY; y <= part.maxY; y += 1) {
      for (let x = part.minX; x <= part.maxX; x += 1) {
        if (parts.labels[(y * frame.width) + x] === part.label
          && (x - part.minX < band || part.maxX - x < band || y - part.minY < band || part.maxY - y < band)) {
          alongEdge += 1;
        }
      }
    }

    const hasDrawingInside = parts.regions.some((other) => other !== part
      && boxWidthOf(other) >= partWidth * active.sheetFrame.inner && boxHeightOf(other) >= partHeight * active.sheetFrame.inner);

    return !(alongEdge >= part.area * active.sheetFrame.edgeShare && hasDrawingInside);
  });

  const extent = parts.regions.filter((part) => kept[part.label]).reduce(
    (box, part) => ({
      minX: Math.min(box.minX, part.minX),
      maxX: Math.max(box.maxX, part.maxX),
      minY: Math.min(box.minY, part.minY),
      maxY: Math.max(box.maxY, part.maxY),
    }),
    {
      minX: frame.width, maxX: -1, minY: frame.height, maxY: -1,
    },
  );

  if (extent.maxX < 0 || (extent.maxX - extent.minX) < frame.width * 0.05 || (extent.maxY - extent.minY) < frame.height * 0.05) {
    return empty;
  }

  // The directions the lines run in besides level and plumb: walls drawn at an angle.
  starting('directions');
  const directions = lineDirectionsOf(lines, active.angledWalls);

  // The width of a line as drawn: the narrower runs of ink, which are the lines seen across. Rows
  // and columns cross a line drawn at an angle aslant, which reads it wider than it is, so a
  // drawing mostly at an angle is also measured turned square to it, and the narrower is right.
  const strokeOf = (bitmap: Bitmap): number => Math.max(1, runLengthPercentileOf(bitmap, 0.25, 2));
  const [main] = directions;
  const stroke = main && main.share >= active.angledWalls.strokeShare
    ? Math.min(strokeOf(lines), strokeOf(rotateBitmap(lines, -main.degrees)))
    : strokeOf(lines);

  const drawing: Drawing = {
    frame,
    parts,
    kept,
    extent,
    stroke,
    directions,
    aspect: turned ? frame.height / frame.width : null,
    turn: {
      degrees: turned ? correction : 0, sourceWidth: width, sourceHeight: height, width: frame.width, height: frame.height,
    },
  };

  const minDim = Math.min(extent.maxX - extent.minX, extent.maxY - extent.minY) + 1;
  const firstGap = Math.max(3, Math.round(minDim * active.openings.defaultShare));
  const first = read(drawing, firstGap);

  if (!first) {
    return empty;
  }

  // Read again with the openings the doors (or failing them, the desks) say a doorway is.
  let doorway: number | null = null;
  if (first.doorWidths.length) doorway = Math.round(medianOf(first.doorWidths) * active.secondRead.doorFactor);
  else if (first.deskLengths.length >= 2) doorway = Math.round(medianOf(first.deskLengths) * active.secondRead.deskFactor);

  let chosen = first;
  if (doorway && Math.abs(doorway - firstGap) > firstGap * active.secondRead.change) {
    reading = 2;
    chosen = read(drawing, Math.max(3, doorway)) ?? first;
  }

  return { sketch: chosen.sketch, ink: chosen.ink() };
};

/**
 * The sketch of a floorplan drawn in `ink` (1 ink, 0 paper), in normalised image coordinates,
 * and the ink itself, squared up as the sketch is. `isFloorPlan` is false when nothing that
 * closes off a building was found.
 */
export const traceDrawing = (ink: Uint8Array, width: number, height: number, options: TraceOptions = {}): TracedDrawing => {
  const [previous, previousListener, previousReading] = [active, listener, reading];
  active = options.config ? traceConfigOf(options.config) : DEFAULT_TRACE_CONFIG;
  listener = options.onStage;
  reading = 1;

  try {
    return traceWith(ink, width, height, options);
  } finally {
    [active, listener, reading] = [previous, previousListener, previousReading];
  }
};

/** The sketch alone: what the tracer found. */
export const traceSketch = (ink: Uint8Array, width: number, height: number, options: TraceOptions = {}): FloorPlanSketch => (
  traceDrawing(ink, width, height, options).sketch);

export default traceSketch;
